import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DownloadFile, FindLoad, FindSuccessItem, previewFile, Images, scheduleViewEnum, token, useModal, User, useSecondaryModal } from 'utils';
import { CustomDelete, CustomizedSwitch, Loader, MinLoader, SimpleTooltip, TextRow } from 'components';
import { adminActions, authActions, httpRequestsOnSuccessActions, systemActions } from 'store';
import { systemItemStyles } from '../../../system/core';
import { getStaffGeneralInfo } from './constants';
import { AddSignature } from './addSignature';
import { myProfileFragment } from '../../../myProfile/core';

const notificationsEnum = {
    EMAIL: 'EMAIL',
    TEXT: 'TEXT',
    PUSH: 'PUSH',
};

const CONNECT_GOOGLE = 'GET_CONNECT_CALENDARS';
const DISCONNECT_CALENDAR = 'DISCONNECT_CALENDAR';
const GET_OFFICE_ALLY = 'GET_OFFICE_ALLY';
const ACTION_TYPE_NOTIFICATIONS = 'CHANGE_NOTIFICATIONS';
const ACTION_TYPE_SCHEDULE_VIEW = 'UPDATE_SCHEDULE_VIEW';

export const StaffSettings = ({ staffGeneral }) => {
    const [google, setGoogle] = useState(false);
    const [notificationSettings, setNotificationSettings] = useState({
        push: false,
        email: false,
        text: false,
    });
    const calendars = useSelector((state) => state.system.calendars);
    const dispatch = useDispatch();
    const classes = systemItemStyles();
    const classesMyProfile = myProfileFragment();
    const { close, open } = useModal();
    const { openSecondary, closeSecondary } = useSecondaryModal();
    const { loadDownload, handleDownload } = DownloadFile();
    const { userInfo } = User();
    const loader = FindLoad(CONNECT_GOOGLE);
    const officeAllyLoader = FindLoad(GET_OFFICE_ALLY);
    const successNotification = FindSuccessItem(ACTION_TYPE_NOTIFICATIONS);
    const loaderNotification = FindLoad(ACTION_TYPE_NOTIFICATIONS);
    const loaderScheduleView = FindLoad(ACTION_TYPE_SCHEDULE_VIEW);

    useEffect(() => {
        dispatch(systemActions.getConnectCalendars());
        dispatch(systemActions.canUseIntegration());
    }, []);

    useEffect(() => {
        if (calendars?.googleCalendar) {
            setGoogle(calendars?.googleCalendar?.isActive);
        }
    }, [calendars]);

    useEffect(() => {
        if (successNotification?.type === ACTION_TYPE_NOTIFICATIONS) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE_NOTIFICATIONS));
        }
    }, [successNotification]);

    useEffect(() => {
        if (userInfo?.notificationSettings && !loaderNotification?.length) {
            setNotificationSettings(userInfo.notificationSettings);
        }
    }, [userInfo]);

    const handleSetCalendarStatus = (type) => {
        setGoogle(!google);
        dispatch(systemActions.calendarStatus({ type: type }));
    };
    const removeCalendar = (type) => {
        const title = type === 'GOOGLE' ? 'Google' : 'Apple';
        open(
            <CustomDelete
                text={`Are you sure you want to disconnect your ${title} Calendar from TherapyLake? This action will remove the synchronization of appointments and events between TherapyLake and your ${title} Calendar.`}
                info={`Disconnect ${title} Calendar?`}
                innerText={'Disconnect'}
                align={'start'}
                handleDel={() => dispatch(systemActions.disconnectCalendar({ type: type }))}
                handleClose={() => close()}
                actionType={DISCONNECT_CALENDAR}
            />
        );
    };

    const connect = (type) => {
        if (type === 'google') {
            // The new Google calendar is created in this time zone — without it Google uses UTC and appts shift
            const timeZone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
            window.open(`${process.env.REACT_APP_API_BASE}/staff/calendar/google?token=${token()}&timeZone=${timeZone}`, '_parent');
        }
    };

    const deleteSignature = () => {
        openSecondary(
            <CustomDelete
                text={`Are you sure you want to delete this signature? This action cannot be undone.`}
                info={`Delete Signature?`}
                handleDel={() => dispatch(adminActions.deleteStaffSignature(staffGeneral?.signature, staffGeneral.id))}
                handleClose={closeSecondary}
                actionType={'DELETE_STAFF_SIGNATURE'}
                afterSuccess={closeSecondary}
                body={
                    <div className="deleting-client-info-box">
                        <img src={Images.messageEdit} alt={'credential'} />
                        <p>{staffGeneral?.signature?.originalName}</p>
                    </div>
                }
            />
        );
    };

    const addSignature = () => {
        open(<AddSignature deleteSignature={deleteSignature} />);
    };

    const changeSettings = (name) => {
        if (name === 'push') {
            dispatch(
                authActions.changeNotifications(userInfo?.id, {
                    type: notificationsEnum?.PUSH,
                    active: !notificationSettings?.push,
                })
            );
        }

        if (name === 'email') {
            dispatch(
                authActions.changeNotifications(userInfo?.id, {
                    type: notificationsEnum?.EMAIL,
                    active: !notificationSettings?.email,
                })
            );
        }

        setNotificationSettings((prev) => ({
            ...prev,
            [name]: !prev[name],
        }));
    };

    const changeScheduleView = (type) => {
        dispatch(authActions.updateScheduleView({ scheduleView: type }));
    };

    if (loader?.length || officeAllyLoader?.length) {
        return <Loader />;
    }

    return (
        <div className="flex-align-start full-width" style={{ gap: 24 }}>
            <div className="my-settings-wrapper">
                <div className="my-settings-card-wrapper">
                    <div className="my-settings-card-header-wrapper">
                        <h4 className="my-settings-card-header">Notification Settings</h4>
                    </div>

                    <div className="my-settings-card-body-wrapper" style={{ padding: '0 16px' }}>
                        <div className={classesMyProfile.notificationInfoBox} style={{ padding: '16px 0' }}>
                            <div>
                                <p className={classesMyProfile.pushTitle}>Receive Push Notifications</p>
                                <p className={classesMyProfile.pushSubTitle}>Get instant updates on your device</p>
                            </div>
                            <CustomizedSwitch checked={notificationSettings?.push}
                                              handleClick={(e) => changeSettings('push')} />
                        </div>
                        <div className={classesMyProfile.notificationInfoBox}
                             style={{ padding: '16px 0', border: 'none' }}>
                            <div>
                                <p className={classesMyProfile.pushTitle}>Receive Email Notifications</p>
                                <p className={classesMyProfile.pushSubTitle}>Stay informed via email</p>
                            </div>
                            <CustomizedSwitch checked={notificationSettings?.email}
                                              handleClick={(e) => changeSettings('email')} />
                        </div>
                    </div>
                </div>

                <div className="my-settings-card-wrapper">
                    <div className="my-settings-card-header-wrapper">
                        <h4 className="my-settings-card-header">Calendar Integration</h4>
                    </div>

                    <div className="my-settings-card-body-wrapper">
                        <div className={classes.calendarBox}>
                            <div className={classes.iconAndTitle}>
                                <img src={Images.googleCalendar} alt="google" />
                                <p>Google Calendar</p>
                            </div>
                            {calendars?.googleCalendar?.isConnected ? (
                                <div className={classes.iconAndTitle}>
                                    <SimpleTooltip title={<p>{'Deactivate Calendar Sync'}</p>} placement="top-start">
                                        <CustomizedSwitch checked={google}
                                                          handleClick={() => handleSetCalendarStatus('GOOGLE')} />
                                    </SimpleTooltip>
                                    <SimpleTooltip title={<p>{'Disconnect Google Calendar'}</p>} placement="top-start">
                                        <button className={classes.removeBtn} onClick={() => removeCalendar('GOOGLE')}>
                                            <img src={Images.remove} alt="icon" />
                                        </button>
                                    </SimpleTooltip>
                                </div>
                            ) : (
                                <button onClick={() => connect('google')} className={classes.connectBtn}>
                                    Connect
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="my-settings-card-wrapper">
                    <div className="my-settings-card-header-wrapper">
                        <h4 className="my-settings-card-header">Signature</h4>
                    </div>

                    <div className="my-settings-card-body-wrapper">
                        {!staffGeneral?.signature ? (
                            <button onClick={addSignature} className="set-staff-password">
                                <img src={Images.addBluePlus} alt="plus" />
                                <p>Add Your Signature</p>
                            </button>
                        ) : (
                            <div className="staff-signature-item-wrapper">
                                <div className="staff-signature-item-info_name">
                                    <SimpleTooltip
                                        title={
                                            'This signature will be automatically used in generated reports where a provider signature is required.'
                                        }
                                        placement="top"
                                        arrow
                                    >
                                        <div className="staff-signature-item-info-img">
                                            <img src={Images.infoCircle} alt="info" />
                                        </div>
                                    </SimpleTooltip>

                                    <p className="staff-signature-item-info-name">
                                        <TextRow
                                            name={staffGeneral?.signature?.originalName}
                                            textWidth={7}
                                            wrapperStyle={{ maxWidth: 200 }}
                                        />
                                    </p>
                                </div>

                                <div className="staff-signature-item-actions-wrapper">
                                    <SimpleTooltip title={'Replace your signature'} placement="top" arrow>
                                        <button className="staff-signature-item-action-btn" onClick={addSignature}>
                                            <img src={Images.plus} alt="info" />
                                        </button>
                                    </SimpleTooltip>

                                    <SimpleTooltip title={'View your signature'} placement="top" arrow>
                                        <button
                                            className="staff-signature-item-action-btn"
                                            onClick={() => previewFile(staffGeneral?.signature)}
                                        >
                                            <img
                                                src={Images.showEye}
                                                alt="eye"
                                                style={{
                                                    filter: 'brightness(0) saturate(100%) invert(32%) sepia(12%) saturate(918%) hue-rotate(176deg) brightness(92%) contrast(90%)',
                                                }}
                                            />
                                        </button>
                                    </SimpleTooltip>

                                    <SimpleTooltip title={'Download your signature'} placement="top" arrow>
                                        <button
                                            className="staff-signature-item-action-btn"
                                            onClick={() => handleDownload(staffGeneral?.signature)}
                                        >
                                            {loadDownload ? (
                                                <MinLoader margin={'0'} color={'#475467'} position={'relative'} />
                                            ) : (
                                                <img
                                                    src={Images.downloadCloudBlue}
                                                    alt="download"
                                                    style={{
                                                        filter: 'brightness(0) saturate(100%) invert(32%) sepia(12%) saturate(918%) hue-rotate(176deg) brightness(92%) contrast(90%)',
                                                    }}
                                                />
                                            )}
                                        </button>
                                    </SimpleTooltip>

                                    <SimpleTooltip title={'Delete your signature'} placement="top" arrow>
                                        <button className="staff-signature-item-action-btn" onClick={deleteSignature}>
                                            <img
                                                src={Images.trash}
                                                alt="trash"
                                                style={{
                                                    filter: 'brightness(0) saturate(100%) invert(24%) sepia(100%) saturate(1921%) hue-rotate(356deg) brightness(95%) contrast(99%)',
                                                }}
                                            />
                                        </button>
                                    </SimpleTooltip>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>


            <div className="my-settings-wrapper">
                <div className="my-settings-card-wrapper">
                    <div className="my-settings-card-header-wrapper">
                        <h4 className="my-settings-card-header">View Settings</h4>
                    </div>

                    <div className="my-settings-card-body-wrapper" style={{ padding: '0 16px' }}>
                        <div className={classesMyProfile.notificationInfoBox} style={{ padding: '16px 0', border: 'none' }}>
                            <div>
                                <p className={classesMyProfile.pushTitle}>Schedule View</p>
                                <p className={classesMyProfile.pushSubTitle}>Choose your default view for the schedule page</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    style={{width: 75}}
                                    className={`schedule-view-setting-btn ${(!userInfo?.scheduleView || userInfo?.scheduleView === scheduleViewEnum.LIST) ? 'active' : ''}`}
                                    onClick={() => changeScheduleView(scheduleViewEnum.LIST)}
                                    disabled={!!loaderScheduleView?.length}
                                >
                                    {loaderScheduleView?.length && userInfo?.scheduleView !== scheduleViewEnum.LIST ? (
                                        <MinLoader margin={'0 auto'} color={'#475467'} position={'relative'} />
                                    ) : (
                                        <>
                                            <img src={Images.scheduleList} alt="list" />
                                            <span>List</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    style={{width: 110}}
                                    className={`schedule-view-setting-btn ${userInfo?.scheduleView === scheduleViewEnum.CALENDAR ? 'active' : ''}`}
                                    onClick={() => changeScheduleView(scheduleViewEnum.CALENDAR)}
                                    disabled={!!loaderScheduleView?.length}
                                >
                                    {loaderScheduleView?.length && userInfo?.scheduleView !== scheduleViewEnum.CALENDAR ? (
                                        <MinLoader margin={'0 auto'} color={'#475467'} position={'relative'} />
                                    ) : (
                                        <>
                                            <img src={Images.scheduleGroup} alt="calendar" />
                                            <span>Calendar</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
